import { useState, useEffect, useRef } from 'react';
import { apiFetch, ApiError } from '../api';

function SEOOptimizer() {
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [optimizedResults, setOptimizedResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [siteUrl, setSiteUrl] = useState('https://delanci.com/');
  const [pageInfo, setPageInfo] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  // 已访问页所用的 page_info 参数栈：栈顶即上一页加载所用的参数
  const [pageHistory, setPageHistory] = useState([]);
  // 当前页加载所用的 page_info 参数（用 ref，异步 fetch 期间也能读到准确值）
  const currentPageParamRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async (pageInfoParam = null) => {
    currentPageParamRef.current = pageInfoParam;
    setPageLoading(true);
    try {
      const query = pageInfoParam ? `&page_info=${encodeURIComponent(pageInfoParam)}` : '';
      const data = await apiFetch(`/api/products?limit=50${query}`);
      setApiError(null);
      setProducts(data.products || []);
      setPageInfo(data.nextPageInfo || null);
      setHasNextPage(!!data.hasNextPage);
    } catch (error) {
      console.error('Fetch products error:', error);
      setApiError(error instanceof ApiError
        ? error.message
        : '网络连接失败，请检查网络设置');
      setProducts([]);
    } finally {
      setPageLoading(false);
    }
  };

  const handleNextPage = () => {
    // 防重入：page_info 在请求完成前不会更新，连点会重复加载同一页
    if (!pageInfo || pageLoading) return;
    // 记录"当前页所用的参数"而非 nextPageInfo，回退时才能回到当前页
    setPageHistory(prev => [...prev, currentPageParamRef.current]);
    setSelectedIds([]);
    setOptimizedResults({});
    fetchProducts(pageInfo);
  };

  const handlePrevPage = () => {
    if (pageHistory.length === 0 || pageLoading) return;
    // 直接读取渲染期 state（无并发更新场景）；避免在 setState updater 里做 fetch 副作用
    const prevPageParam = pageHistory[pageHistory.length - 1];
    setPageHistory(pageHistory.slice(0, -1));
    setSelectedIds([]);
    setOptimizedResults({});
    fetchProducts(prevPageParam);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === products.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(products.map(p => p.id));
    }
  };

  const handleGenerate = async () => {
    if (selectedIds.length === 0) return;

    setLoading(true);
    const selectedProducts = products.filter(p => selectedIds.includes(p.id));
    const results = {};

    let failed = 0;
    for (const product of selectedProducts) {
      try {
        const data = await apiFetch('/api/optimize', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product, siteUrl: siteUrl.trim() || undefined })
        });
        results[product.id] = { ...data, originalTitle: product.title, originalDescription: product.description };
      } catch (error) {
        failed += 1;
        console.error(`Generate SEO error for ${product.title}:`, error);
      }
    }

    setOptimizedResults(results);
    setApiError(failed ? `${failed} 个产品生成失败，成功结果已保留` : null);
    setLoading(false);
  };

  const handleApplyAll = async () => {
    if (Object.keys(optimizedResults).length === 0) return;

    setApplyLoading(true);

    const appliedIds = [];
    let failed = 0;
    for (const [productId, seoData] of Object.entries(optimizedResults)) {
      try {
        await apiFetch('/api/optimize/apply', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productId, seoData })
        });
        appliedIds.push(productId);
      } catch (error) {
        failed += 1;
        console.error(`Apply SEO error for product ${productId}:`, error);
      }
    }

    setOptimizedResults((previous) => {
      const remaining = { ...previous };
      appliedIds.forEach((id) => delete remaining[id]);
      return remaining;
    });
    setSelectedIds((previous) => previous.filter((id) => !appliedIds.includes(String(id))));
    setApiError(failed ? `${failed} 个产品应用失败，失败项已保留` : null);
    setApplyLoading(false);
    if (appliedIds.length) fetchProducts(currentPageParamRef.current);
  };

  const handleApplySingle = async (productId) => {
    const seoData = optimizedResults[productId];
    if (!seoData) return;

    try {
      await apiFetch('/api/optimize/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, seoData })
      });
      setOptimizedResults(prev => {
        const newResults = { ...prev };
        delete newResults[productId];
        return newResults;
      });
      setSelectedIds(prev => prev.filter(id => id !== productId));
      setApiError(null);
      fetchProducts(currentPageParamRef.current);
    } catch (error) {
      console.error('Apply SEO error:', error);
      setApiError(error.message);
    }
  };

  const updateResult = (productId, field, value) => {
    setOptimizedResults(prev => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value }
    }));
  };

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: '100%' }}>
      <h2 style={{ fontSize: '18px', margin: '0 0 20px', color: '#1a1a1a' }}>📊 产品SEO优化工具</h2>

      {apiError && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeeba',
          borderRadius: '4px',
          color: '#856404',
          marginBottom: '20px',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          ⚠️ {apiError}
        </div>
      )}

      <div style={{ marginBottom: '15px', padding: '12px 16px', backgroundColor: '#f0f7ff', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13px', color: '#1565c0' }}>GSC 站点域名（可选）：</span>
        <input
          type="text"
          value={siteUrl}
          onChange={(e) => setSiteUrl(e.target.value)}
          placeholder="https://你的正式域名.com（留空则纯 AI 生成）"
          style={{
            flex: 1,
            minWidth: '250px',
            padding: '6px 10px',
            border: '1px solid #90caf9',
            borderRadius: '4px',
            fontSize: '13px'
          }}
        />
        <span style={{ fontSize: '12px', color: '#666' }}>将从 Google Search Console 拉取真实关键词注入优化</span>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={selectAll}
            style={{
              padding: '8px 15px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            {selectedIds.length === products.length ? '取消全选' : '全选'}
          </button>
          <button
            onClick={handleGenerate}
            disabled={loading || selectedIds.length === 0}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#0084ff',
              color: '#fff',
              cursor: (loading || selectedIds.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '13px'
            }}
          >
            {loading ? '🤖 生成中...' : '🤖 批量生成优化建议'}
          </button>
          <div style={{ fontSize: '14px', color: '#666' }}>
            已选择 <span style={{ color: '#0084ff', fontWeight: 'bold' }}>{selectedIds.length}</span> 个产品
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap', fontSize: '13px', color: '#666' }}>
          <button
            onClick={handlePrevPage}
            disabled={pageHistory.length === 0 || pageLoading}
            style={{
              padding: '6px 14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: (pageHistory.length === 0 || pageLoading) ? '#f5f5f5' : '#fff',
              color: (pageHistory.length === 0 || pageLoading) ? '#bbb' : '#333',
              cursor: (pageHistory.length === 0 || pageLoading) ? 'not-allowed' : 'pointer',
              fontSize: '13px'
            }}
          >
            ← 上一页
          </button>
          <span>{pageLoading ? '⏳ 加载中...' : `第 ${pageHistory.length + 1} 页`}</span>
          <button
            onClick={handleNextPage}
            disabled={!hasNextPage || pageLoading}
            style={{
              padding: '6px 14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: (!hasNextPage || pageLoading) ? '#f5f5f5' : '#fff',
              color: (!hasNextPage || pageLoading) ? '#bbb' : '#333',
              cursor: (!hasNextPage || pageLoading) ? 'not-allowed' : 'pointer',
              fontSize: '13px'
            }}
          >
            下一页 →
          </button>
          <span>本页 {products.length} 个商品</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {products.map(product => {
          const isSelected = selectedIds.includes(product.id);
          const result = optimizedResults[product.id];

          return (
            <div
              key={product.id}
              onClick={() => toggleSelect(product.id)}
              style={{
                border: `2px solid ${isSelected ? '#0084ff' : '#eee'}`,
                borderRadius: '8px',
                padding: '8px 10px',
                cursor: 'pointer',
                backgroundColor: isSelected ? '#f8fcff' : '#fff',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => e.stopPropagation()}
                style={{ width: '18px', height: '18px', flexShrink: 0 }}
              />

              {product.image ? (
                <img
                  src={product.image}
                  alt={product.title}
                  style={{
                    width: '30px',
                    height: '30px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    flexShrink: 0,
                    backgroundColor: '#f5f5f5'
                  }}
                />
              ) : (
                <div
                  aria-label="无商品图片"
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '4px',
                    flexShrink: 0,
                    backgroundColor: '#f5f5f5'
                  }}
                />
              )}

              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ margin: '0 0 5px', fontSize: '15px', fontWeight: '500', color: '#333' }}>
                  {product.title}
                </h4>
                <p style={{ margin: '0', fontSize: '13px', color: '#666', lineHeight: '1.4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {product.description}
                </p>
                {result && (
                  <span style={{ padding: '3px 8px', backgroundColor: '#28a745', color: '#fff', borderRadius: '10px', fontSize: '12px', marginLeft: '5px' }}>
                    已优化
                  </span>
                )}
              </div>

              {result && (
                <div style={{ padding: '12px 18px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e3f2fd', flex: 1, minWidth: '400px' }}>
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>SEO标题</div>
                    <input
                      type="text"
                      value={result.title}
                      onChange={(e) => {
                        e.stopPropagation();
                        updateResult(product.id, 'title', e.target.value);
                      }}
                      style={{
                        width: '100%',
                        padding: '5px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>元描述</div>
                    <textarea
                      value={result.metaDescription}
                      onChange={(e) => {
                        e.stopPropagation();
                        updateResult(product.id, 'metaDescription', e.target.value);
                      }}
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '5px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '12px',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                  {result.realKeywords && result.realKeywords.length > 0 && (
                    <div style={{ marginBottom: '6px' }}>
                      <div style={{ fontSize: '11px', color: '#1565c0', marginBottom: '2px' }}>
                        基于 GSC 真实关键词（展示量/点击/排名）
                      </div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.5' }}>
                        {result.realKeywords.map(k =>
                          `${k.keyword}（${k.impressions}次/${k.clicks}点击/第${k.position ?? '—'}位）`
                        ).join('、')}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApplySingle(product.id);
                    }}
                    style={{
                      padding: '5px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: '#28a745',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    ✓ 应用
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(optimizedResults).length > 0 && (
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px', textAlign: 'center' }}>
          <div style={{ marginBottom: '10px', fontSize: '14px', color: '#333' }}>
            已生成 <span style={{ color: '#0084ff', fontWeight: 'bold' }}>{Object.keys(optimizedResults).length}</span> 个产品的优化建议
          </div>
          <button
            onClick={handleApplyAll}
            disabled={applyLoading}
            style={{
              padding: '12px 30px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#28a745',
              color: '#fff',
              cursor: applyLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            {applyLoading ? '⏳ 应用中...' : '✓ 一键应用所有优化'}
          </button>
        </div>
      )}
    </div>
  );
}

export default SEOOptimizer;
